<template>
  <teleport to="body">
    <div
      ref="sourceHolder"
      class="source-holder px-4 py-3 z-[9999] fixed bg-white/95 dark:bg-[#0d1a20]/95 backdrop-blur-xl border border-gray-300 dark:border-gray-700 text-gray-900 dark:text-gray-100 shadow-2xl rounded-xl transition-opacity duration-200"
      :class="isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'"
      :style="{
        width: width + 'px',
        visibility: isVisible ? 'visible' : 'hidden',
      }"
      :data-ref-id="refId"
      @mouseenter="mouseIsOver = true"
      @mouseleave="mouseIsOver = false"
    >
      <div v-if="loading" class="text-center py-6">
        <i
          class="text-2xl fas fa-circle-notch fa-spin"
          :class="isPatent ? 'text-purple-500' : 'text-blue-500'"
        ></i>
      </div>
      <div v-else-if="selectedSource">
        <div class="flex items-start mb-3">
          <div
            class="font-mono font-bold w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mr-3 text-sm shadow-sm cursor-pointer"
            :class="
              isPatent ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'
            "
            @click="scrollToCitation(refId)"
          >
            {{ refId }}
          </div>
          <div class="overflow-hidden">
            <h3
              class="font-bold text-xs leading-snug cursor-pointer hover:underline transition-colors line-clamp-3"
              :class="
                isPatent ? 'hover:text-purple-600' : 'hover:text-blue-600'
              "
              @click="scrollToCitation(refId)"
              v-html="
                isPatent ? selectedSource.title : selectedSource.metadata.title
              "
            />
          </div>
        </div>

        <div class="flex flex-wrap items-center gap-1.5 mb-3">
          <template v-if="!isPatent">
            <span
              v-if="selectedSource.metadata.year"
              class="bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 px-1.5 py-0.5 rounded font-mono font-bold text-[9px] border border-gray-200 dark:border-gray-700"
            >
              {{ selectedSource.metadata.year }}
            </span>
            <span
              v-if="selectedSource.metadata.journal"
              class="bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50 px-1.5 py-0.5 rounded text-[9px] font-bold truncate max-w-[140px]"
            >
              {{ selectedSource.metadata.journal }}
            </span>
          </template>
          <template v-else>
            <span
              v-if="selectedSource.assignee"
              class="bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-blue-800/50 px-1.5 py-0.5 rounded text-[9px] font-bold"
            >
              {{ selectedSource.assignee }}
            </span>
          </template>
        </div>

        <div
          class="flex justify-end pt-2 border-t border-gray-100 dark:border-gray-800"
        >
          <a
            v-if="detailsUrl"
            target="_blank"
            :href="detailsUrl"
            class="text-[10px] font-bold hover:underline flex items-center gap-1"
            :class="
              isPatent
                ? 'text-purple-600 dark:text-purple-400'
                : 'text-blue-600 dark:text-blue-400'
            "
          >
            View Details <i class="fas fa-arrow-right text-[8px]"></i>
          </a>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
import { createPopper } from "@popperjs/core";
import * as utils from "../../utils";

const { sqidEncode, sqidEncodeString } = utils;

export default {
  name: "ResearchCompassSourcePopover",
  props: {
    sources: Array,
    patents: Array,
    isPatent: Boolean,
    width: {
      type: Number,
      default: 280,
    },
  },
  emits: ["scroll-to-citation"],
  data() {
    return {
      isVisible: false,
      mouseIsOver: false,
      loading: true,
      refId: null,
      selectedSource: null,
      popperInstance: null,
    };
  },
  computed: {
    detailsUrl() {
      const webBaseUrl = import.meta.env.VITE_WEB_BASE_URL || "";
      if (!this.selectedSource) return "";
      if (this.isPatent) {
        if (!this.selectedSource.patentId) return "";
        return `${webBaseUrl}/research-compass/patent/${sqidEncodeString(
          this.selectedSource.patentId,
        )}`;
      }
      if (!this.selectedSource.id) return "";
      return `${webBaseUrl}/research-compass/paper/${sqidEncode(
        this.selectedSource.id,
      )}`;
    },
  },
  watch: {
    mouseIsOver(newVal) {
      if (!newVal) {
        setTimeout(() => {
          if (!this.mouseIsOver) {
            this.hide();
          }
        }, 200);
      }
    },
  },
  methods: {
    show(targetRect) {
      this.isVisible = true;
      this.mouseIsOver = true;

      const virtualElement = {
        getBoundingClientRect: () => targetRect,
      };

      if (this.popperInstance) {
        this.popperInstance.destroy();
      }

      this.$nextTick(() => {
        if (!this.$refs.sourceHolder) return;
        this.popperInstance = createPopper(
          virtualElement,
          this.$refs.sourceHolder,
          {
            strategy: "fixed",
            placement: "top",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 2],
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["bottom", "top"],
                },
              },
              {
                name: "preventOverflow",
                options: {
                  boundary: document.body,
                  padding: 16,
                },
              },
            ],
          },
        );
      });
    },
    hide() {
      this.isVisible = false;
      if (this.popperInstance) {
        this.popperInstance.destroy();
        this.popperInstance = null;
      }
    },
    hideIfNotHover() {
      if (!this.mouseIsOver) {
        this.hide();
      }
    },
    load() {
      this.loading = true;
      if (this.isPatent) {
        this.selectedSource = (this.patents || [])[parseInt(this.refId) - 1];
      } else {
        this.selectedSource = (this.sources || [])[parseInt(this.refId) - 1];
      }
      this.loading = false;
    },
    scrollToCitation(refId) {
      this.$emit("scroll-to-citation", refId);
      this.hide();
    },
  },
};
</script>
